import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BorderColor as BorderColorIcon } from "../../../../components/icons";
import ClaimTabs from "../../claims/common/ClaimTabs";
import activePolicy from "../../../../assets/images/activePolicy.svg";
import inactivePolicy from "../../../../assets/images/inactivePolicy.svg";
import activeDeceased from "../../../../assets/images/activeDeceased.svg";
import inactiveDeceased from "../../../../assets/images/inactiveDeceased.svg";
import activePricing from "../../../../assets/images/activePricing.svg";
import inactivePricing from "../../../../assets/images/inactivePricing.svg";
import activeLocation from "../../../../assets/images/activeLocation.svg";
import inactiveLocation from "../../../../assets/images/inactiveLocation.svg";
import activeBanking from "../../../../assets/images/activeBanking.svg";
import inactiveBanking from "../../../../assets/images/inactiveBanking.svg";
import Input from "../../../../components/Input";
import {
  TombstoneItem,
  getTombstoneStatus,
  useTombstoneStore,
} from "../../../../store/useTombstoneStore";
import { useAuthStore } from "../../../../store/useAuthStore";
import {
  OperationsPageShell,
  OperationsSectionHeader,
  OperationsDetailField,
  OperationsDetailGrid,
  formatOperationsStatus,
  getOperationsStatusSx,
  operationsActionBarSx,
  operationsDetailPaperSx,
} from "../../ifas/components/OperationsUI";

const numberFields = ["stonePrice", "kilometerCharge", "totalPrice"];

const formatDateInput = (value?: string) => {
  if (!value) return "";
  return value.includes("T") ? value.split("T")[0] : value;
};

const getDisplayName = (
  firstName?: string,
  lastName?: string,
  userName?: string,
) => {
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  return fullName || userName || "";
};

const buildPayload = (item: TombstoneItem, consultantName: string) => ({
  formDate: formatDateInput(item.formDate),
  branchName: item.branchName || "",
  consultantName: consultantName || item.consultantName || "",
  policyNumber: item.policyNumber || "",
  invoiceNumber: item.invoiceNumber || "",
  clientSurname: item.clientSurname || "",
  clientNames: item.clientNames || "",
  clientResidentialAddress: item.clientResidentialAddress || "",
  clientContactNumbers: item.clientContactNumbers || "",
  deceasedSurname: item.deceasedSurname || "",
  deceasedNames: item.deceasedNames || "",
  deceasedDateOfBirth: formatDateInput(item.deceasedDateOfBirth),
  deceasedDateOfDeath: formatDateInput(item.deceasedDateOfDeath),
  tombstoneMessage: item.tombstoneMessage || "",
  stoneType: item.stoneType || "",
  stoneCode: item.stoneCode || "",
  stonePrice: Number(item.stonePrice) || 0,
  kilometerCharge: Number(item.kilometerCharge) || 0,
  totalPrice: Number(item.totalPrice) || 0,
  installationDate: formatDateInput(item.installationDate),
  unveilingDate: formatDateInput(item.unveilingDate),
  cemeteryName: item.cemeteryName || "",
  installationPlace: item.installationPlace || "",
  graveNumber: item.graveNumber || "",
  accessNotes: item.accessNotes || "",
  internalNotes: item.internalNotes || "",
  clientNotes: item.clientNotes || "",
});

export default function TombstoneDetails({
  onUpdate,
  onConfirm,
  isSaving,
  isConfirming,
  onBackToList,
}: any) {
  const { selectedTombstone, updateSelectedTombstone } = useTombstoneStore();
  const { first_name, last_name, user_name } = useAuthStore();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editSnapshot, setEditSnapshot] = useState<TombstoneItem | null>(null);
  const consultantName = getDisplayName(first_name, last_name, user_name);

  const tabData = [
    {
      id: 0,
      icon: { active: activePolicy, inactive: inactivePolicy },
      alt: "Request Information",
    },
    {
      id: 1,
      icon: { active: activeDeceased, inactive: inactiveDeceased },
      alt: "Client & Deceased",
    },
    {
      id: 2,
      icon: { active: activePricing, inactive: inactivePricing },
      alt: "Stone & Pricing",
    },
    {
      id: 3,
      icon: { active: activeLocation, inactive: inactiveLocation },
      alt: "Installation Details",
    },
    {
      id: 4,
      icon: { active: activeBanking, inactive: inactiveBanking },
      alt: "Banking Details",
    },
  ];

  const status = useMemo(
    () => getTombstoneStatus(selectedTombstone),
    [selectedTombstone],
  );

  useEffect(() => {
    setActiveTab(0);
    setIsEditMode(false);
    setEditSnapshot(null);
  }, [selectedTombstone?.id, selectedTombstone?.tombstoneId]);

  if (!selectedTombstone) {
    return (
      <Box p={3} bgcolor="background.paper">
        <Typography fontSize={24} fontWeight={600} color="primary.main">
          Tombstone details not found
        </Typography>
      </Box>
    );
  }

  const handleChange =
    (field: keyof TombstoneItem) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (numberFields.includes(String(field))) {
        const numericValue = value === "" ? "" : Number(value);
        const updatedData: Partial<TombstoneItem> = {
          [field]: numericValue,
        };

        if (field === "stonePrice" || field === "kilometerCharge") {
          const stone =
            field === "stonePrice"
              ? Number(value || 0)
              : Number(selectedTombstone.stonePrice || 0);
          const km =
            field === "kilometerCharge"
              ? Number(value || 0)
              : Number(selectedTombstone.kilometerCharge || 0);
          updatedData.totalPrice = stone + km;
        }

        updateSelectedTombstone(updatedData);
        return;
      }

      updateSelectedTombstone({
        [field]: value,
      });
    };

  const inputProps = (
    field: keyof TombstoneItem,
    label: string,
    options?: {
      type?: string;
      multiline?: boolean;
      maxLength?: number;
      height?: number;
      disabled?: boolean;
      rows?: number;
    },
  ) => ({
    value:
      options?.type === "date"
        ? formatDateInput(String(selectedTombstone[field] || ""))
        : selectedTombstone[field] ?? "",
    onChange: handleChange(field),
    type: options?.type || "text",
    height: options?.height || 42,
    width: "100%",
    label,
    disabled: options?.disabled ?? !isEditMode,
    backgroundColor:
      options?.disabled || !isEditMode ? "background.paper" : "#FFFFFF",
    multiline: options?.multiline,
    rows: options?.rows || 3,
    inputProps: options?.maxLength
      ? { maxLength: options.maxLength }
      : undefined,
    helperText: options?.maxLength
      ? `${String(selectedTombstone[field] || "").length}/${options.maxLength}`
      : undefined,
  });

  const handleSave = async () => {
    await onUpdate(buildPayload(selectedTombstone, consultantName));
    setIsEditMode(false);
    setEditSnapshot(null);
  };

  const handleCancelEdit = () => {
    if (editSnapshot) {
      updateSelectedTombstone(editSnapshot);
    }
    setIsEditMode(false);
    setEditSnapshot(null);
  };

  const handleEditClick = () => {
    setEditSnapshot({ ...selectedTombstone });
    setActiveTab(activeTab === 0 ? 1 : activeTab);
    setIsEditMode(true);
  };

  const renderTitle = () => tabData[activeTab]?.alt || "";

  const renderRequestInfo = () => (
    <OperationsDetailGrid>
      <OperationsDetailField
        label="Tombstone Number"
        value={selectedTombstone.tombstoneNumber}
      />
      <OperationsDetailField
        label="ICE Claim Number"
        value={selectedTombstone.iceClaimNumber}
      />
      <OperationsDetailField
        label="Status"
        value={formatOperationsStatus(status)}
      />
      <OperationsDetailField
        label="Form Date"
        value={formatDateInput(selectedTombstone.formDate)}
      />
      <OperationsDetailField
        label="Branch Name"
        value={selectedTombstone.branchName}
      />
      <OperationsDetailField
        label="Consultant Name"
        value={consultantName || selectedTombstone.consultantName}
      />
      <OperationsDetailField
        label="Policy Number"
        value={selectedTombstone.policyNumber}
      />
      <OperationsDetailField
        label="Invoice Number"
        value={selectedTombstone.invoiceNumber}
      />
    </OperationsDetailGrid>
  );

  const renderClientAndDeceased = () => {
    if (!isEditMode) {
      return (
        <OperationsDetailGrid>
          <OperationsDetailField
            label="Claimant First Name"
            value={selectedTombstone.clientNames}
          />
          <OperationsDetailField
            label="Claimant Surname"
            value={selectedTombstone.clientSurname}
          />
          <OperationsDetailField
            label="Contact Number"
            value={selectedTombstone.clientContactNumbers}
          />
          <OperationsDetailField
            label="Deceased Name"
            value={selectedTombstone.deceasedNames}
          />
          <OperationsDetailField
            label="Deceased Surname"
            value={selectedTombstone.deceasedSurname}
          />
          <OperationsDetailField
            label="Date of Birth"
            value={formatDateInput(selectedTombstone.deceasedDateOfBirth)}
          />
          <OperationsDetailField
            label="Date of Death"
            value={formatDateInput(selectedTombstone.deceasedDateOfDeath)}
          />
        </OperationsDetailGrid>
      );
    }

    return (
    <Grid container rowSpacing={3} columnSpacing={3} mt={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("clientNames", "Claimant First Name")} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("clientSurname", "Claimant Surname")} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("clientContactNumbers", "Contact Number")} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("deceasedNames", "Deceased Name")} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("deceasedSurname", "Deceased Surname")} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          {...inputProps("deceasedDateOfBirth", "Date of Birth", {
            type: "date",
          })}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          {...inputProps("deceasedDateOfDeath", "Date of Death", {
            type: "date",
          })}
        />
      </Grid>
    </Grid>
    );
  };

const renderStonePricing = () => {
  if (!isEditMode) {
    return (
      <OperationsDetailGrid>
        <OperationsDetailField label="Stone Type" value={selectedTombstone.stoneType} />
        <OperationsDetailField label="Stone Code" value={selectedTombstone.stoneCode} />
        <OperationsDetailField label="Stone Price" value={selectedTombstone.stonePrice} />
        <OperationsDetailField
          label="Kilometer Charge"
          value={selectedTombstone.kilometerCharge}
        />
        <OperationsDetailField label="Total Price" value={selectedTombstone.totalPrice} />
        <OperationsDetailField
          label="Tombstone Message"
          value={selectedTombstone.tombstoneMessage}
          span={3}
        />
      </OperationsDetailGrid>
    );
  }

  return (
  <Grid container rowSpacing={3} columnSpacing={3} mt={2}>
    <Grid size={{ xs: 12, md: 4 }}>
      <Input {...inputProps("stoneType", "Stone Type")} />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <Input {...inputProps("stoneCode", "Stone Code")} />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <Input
        {...inputProps("stonePrice", "Stone Price", { type: "number" })}
      />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <Input
        {...inputProps("kilometerCharge", "Kilometer Charge", {
          type: "number",
        })}
      />
    </Grid>
    <Grid size={{ xs: 12, md: 4 }}>
      <Input
        {...inputProps("totalPrice", "Total Price", { type: "number" })}
      />
    </Grid>
      <Grid size={{ xs: 12 }}>
      <TextField
        disabled={!isEditMode}
        label="Tombstone Message"
        value={selectedTombstone.tombstoneMessage || ""}
        onChange={(e) => updateSelectedTombstone({ tombstoneMessage: e.target.value })}
        multiline
        rows={4}
        fullWidth
        inputProps={{ maxLength: 300 }}
        helperText={`${(selectedTombstone.tombstoneMessage || "").length}/300`}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: isEditMode ? "#ffffff" : "#F7F7F7",
          }
        }}
      />
    </Grid>
  </Grid>
  );
};

  const renderInstallation = () => {
    if (!isEditMode) {
      return (
        <OperationsDetailGrid>
          <OperationsDetailField
            label="Installation Date"
            value={formatDateInput(selectedTombstone.installationDate)}
          />
          <OperationsDetailField
            label="Unveiling Date"
            value={formatDateInput(selectedTombstone.unveilingDate)}
          />
          <OperationsDetailField
            label="Cemetery Name"
            value={selectedTombstone.cemeteryName}
          />
          <OperationsDetailField
            label="Installation Place"
            value={selectedTombstone.installationPlace}
          />
          <OperationsDetailField
            label="Grave Number"
            value={selectedTombstone.graveNumber}
          />
          <OperationsDetailField
            label="Access Notes"
            value={selectedTombstone.accessNotes}
            span={3}
          />
          <OperationsDetailField
            label="Internal Notes"
            value={selectedTombstone.internalNotes}
            span={2}
          />
          <OperationsDetailField
            label="Client Notes"
            value={selectedTombstone.clientNotes}
          />
        </OperationsDetailGrid>
      );
    }

    return (
    <Grid container rowSpacing={3} columnSpacing={3} mt={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          {...inputProps("installationDate", "Installation Date", {
            type: "date",
          })}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          {...inputProps("unveilingDate", "Unveiling Date", { type: "date" })}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("cemeteryName", "Cemetery Name")} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("installationPlace", "Installation Place")} />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input {...inputProps("graveNumber", "Grave Number")} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Input
          {...inputProps("accessNotes", "Access Notes", {
            multiline: true,
            height: 90,
            maxLength: 300,
          })}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Input
          {...inputProps("internalNotes", "Internal Notes", {
            multiline: true,
            height: 90,
            maxLength: 300,
          })}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Input
          {...inputProps("clientNotes", "Client Notes", {
            multiline: true,
            height: 90,
            maxLength: 300,
          })}
        />
      </Grid>
    </Grid>
    );
  };

  const renderBankingDetails = () => (
    <OperationsDetailGrid>
      <OperationsDetailField label="Account Name" value={selectedTombstone.accountName} />
      <OperationsDetailField label="Bank" value={selectedTombstone.bank} />
      <OperationsDetailField
        label="Account Number"
        value={selectedTombstone.accountNumber}
      />
      <OperationsDetailField label="Branch Code" value={selectedTombstone.branchCode} />
      <OperationsDetailField label="Reference" value={selectedTombstone.reference} />
    </OperationsDetailGrid>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderRequestInfo();
      case 1:
        return renderClientAndDeceased();
      case 2:
        return renderStonePricing();
      case 3:
        return renderInstallation();
      case 4:
        return renderBankingDetails();
      default:
        return renderRequestInfo();
    }
  };

  return (
    <OperationsPageShell
      eyebrow="Tombstone Details"
      title={renderTitle().trim()}
      description="Review request information, client details, stone pricing, installation plans, and banking references from the selected tombstone record."
      meta={
        <>
          <Chip
            label={selectedTombstone.tombstoneNumber || "TOM-000000"}
            size="small"
            sx={{ fontWeight: 800, borderRadius: 1.5 }}
          />
          <Chip
            label={formatOperationsStatus(status)}
            size="small"
            sx={{
              ...getOperationsStatusSx(status),
              fontWeight: 800,
              borderRadius: 1.5,
            }}
          />
        </>
      }
      actions={
        <Box sx={operationsActionBarSx}>
          {!isEditMode ? (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onBackToList}
                disabled={isSaving || isConfirming}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onConfirm}
                disabled={isSaving || isConfirming}
              >
                {isConfirming ? "Confirming..." : "Confirm Job"}
              </Button>
              <Tooltip title="Edit tombstone details">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleEditClick}
                  disabled={isSaving || isConfirming}
                  sx={{ minWidth: 42, px: 1 }}
                >
                  <BorderColorIcon fontSize="small" />
                </Button>
              </Tooltip>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelEdit}
                disabled={isSaving || isConfirming}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSave}
                disabled={isSaving || isConfirming}
              >
                {isSaving ? "Updating..." : "Update"}
              </Button>
            </>
          )}
        </Box>
      }
    >
      <Paper
        sx={operationsDetailPaperSx}
        elevation={0}
      >
        <ClaimTabs
          tabs={tabData}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        
        <Box mt={3}>
          {renderTabContent()}
        </Box>
      </Paper>
    </OperationsPageShell>
  );
}
